import HeroBackground from 'components/HeroBackground';
import { useHeroScrollMotion } from 'hooks/useHeroScrollMotion';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Activity, ArrowLeft, Building2, Check, Layers, LineChart, Loader2, Search, TrendingUp, Users } from 'lucide-react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { BLOG_VISUAL_THEMES, formatBlogDate, getPublishedBlogPostBySlug } from 'lib/blogCms';
import { CaseNav, GridGlow, Watermark, fadeUp, staggerContainer, viewportOnce } from 'components/MagellanPrototypePrimitives';

const DEFAULT_HERO_IMAGE = '/images/blog/blog-hero-office.png';

const THEME_DETAILS = {
  'editorial-parallax': {
    pageBg: '#050505',
    panelBg: '#0a0a0a',
    label: 'Editorial Dossier',
    watermark: '01',
    heroMode: 'parallax',
  },
  'wine-split': {
    pageBg: '#080406',
    panelBg: '#3a0f1d',
    label: 'Operating Note',
    watermark: '3.1x',
    heroMode: 'split',
  },
  'data-room': {
    pageBg: '#060709',
    panelBg: '#0a0c0e',
    label: 'Live Data Room',
    watermark: '000s',
    heroMode: 'terminal',
  },
  'process-index': {
    pageBg: '#070a0c',
    panelBg: '#0b1115',
    label: 'Process File',
    watermark: 'Flow',
    heroMode: 'process',
  },
};

function renderParagraphs(body) {
  return String(body || '')
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);
}

function sanitizeTheme(value) {
  const candidate = String(value || '').trim();
  return BLOG_VISUAL_THEMES.some((theme) => theme.value === candidate) ? candidate : 'editorial-parallax';
}

function sanitizeAccent(value, themeKey) {
  const color = String(value || '').trim();
  if (['#d4af37', '#e9c46a', '#4c8dcc', '#34d399', '#7dd3fc'].includes(color.toLowerCase())) return '#b6c0ca';
  if (/^#[0-9a-f]{6}$/i.test(color)) return color.toLowerCase();
  return '#b6c0ca';
}

function getOneSentence(value, fallback = '') {
  const text = String(value || fallback || '').trim();
  if (!text) return '';
  const match = text.match(/^(.+?[.!?])(\s|$)/);
  return match ? match[1] : text;
}

function estimateReadMinutes(paragraphs) {
  const words = paragraphs.join(' ').split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.ceil(words / 210));
}

function getPairItems(items, firstKey = 'label', secondKey = 'value') {
  return Array.isArray(items)
    ? items
      .map((item) => [
        String(item?.[firstKey] || item?.title || item?.heading || '').trim(),
        String(item?.[secondKey] || item?.detail || item?.body || '').trim(),
      ])
      .filter(([label, value]) => label && value)
    : [];
}

function getMetaItems(post, paragraphs) {
  const structured = getPairItems(post.metaBlocks);
  if (structured.length) return structured.slice(0, 4);
  return [
    ['Category', post.category || 'Blog'],
    ['Author', post.authorName || 'Magellan'],
    ['Published', formatBlogDate(post.publishedAt || post.updatedAt) || 'Published'],
    ['Read time', `${estimateReadMinutes(paragraphs)} min`],
  ];
}

function getHeroStat(post, paragraphs) {
  if (post.heroStatValue || post.heroStatLabel) {
    return {
      value: post.heroStatValue || `${estimateReadMinutes(paragraphs)}m`,
      label: post.heroStatLabel || 'operating read',
    };
  }
  return {
    value: `${estimateReadMinutes(paragraphs)}m`,
    label: 'operating read',
  };
}

function getMetrics(post, paragraphs) {
  const structured = getPairItems(post.metrics);
  if (structured.length) return structured.map(([label, value]) => [value, label]).slice(0, 4);
  const tags = Array.isArray(post.tags) ? post.tags : [];
  const stat = getHeroStat(post, paragraphs);
  return [
    [stat.value, stat.label],
    [String(paragraphs.length).padStart(2, '0'), 'field notes'],
    [String(tags.length || 1).padStart(2, '0'), 'topic signals'],
    [post.category || 'Ops', 'category'],
  ];
}

function getPillars(post, paragraphs) {
  const icons = [Search, Layers, TrendingUp, Activity, Building2, Users];
  const structured = Array.isArray(post.pillars)
    ? post.pillars
      .map((pillar, index) => ({
        icon: icons[index % icons.length],
        title: String(pillar?.title || '').trim(),
        detail: String(pillar?.detail || '').trim(),
      }))
      .filter((pillar) => pillar.title && pillar.detail)
    : [];
  if (structured.length) return structured.slice(0, 6);

  const tags = Array.isArray(post.tags) ? post.tags : [];
  const lead = getOneSentence(post.excerpt, paragraphs[0]);
  return [
    {
      icon: Search,
      title: 'Diagnose',
      detail: lead || 'Find the operating pressure before it turns into another urgent fix.',
    },
    {
      icon: Layers,
      title: tags[0] || 'Structure',
      detail: paragraphs[1] || 'Turn the issue into visible steps, ownership, and repeatable follow-up.',
    },
    {
      icon: TrendingUp,
      title: tags[1] || 'Control',
      detail: paragraphs[2] || 'Use the note to decide what needs to be reviewed, assigned, and documented.',
    },
  ];
}

function getChapters(post, paragraphs) {
  const contentRows = Array.isArray(post.contentRows)
    ? post.contentRows
      .map((row, index) => ({
        number: String(index + 1).padStart(2, '0'),
        eyebrow: String(row?.eyebrow || '').trim(),
        heading: String(row?.heading || '').trim(),
        leftText: String(row?.leftText || '').trim(),
        body: String(row?.body || '').trim(),
      }))
      .filter((row) => row.heading || row.leftText || row.body)
    : [];
  if (contentRows.length) return contentRows;

  const structured = Array.isArray(post.chapters)
    ? post.chapters
      .map((chapter, index) => ({
        number: String(index + 1).padStart(2, '0'),
        eyebrow: '',
        heading: String(chapter?.heading || '').trim(),
        leftText: '',
        body: String(chapter?.body || '').trim(),
      }))
      .filter((chapter) => chapter.heading && chapter.body)
    : [];
  if (structured.length) return structured;

  const headings = ['The Pressure', 'What Usually Breaks', 'How To Read It', 'What To Build Next'];
  const source = paragraphs.length ? paragraphs : ['This operating note is being prepared.'];
  const chapters = [];
  for (let index = 0; index < source.length; index += 2) {
    chapters.push({
      number: String(chapters.length + 1).padStart(2, '0'),
      eyebrow: '',
      heading: headings[chapters.length % headings.length],
      leftText: '',
      body: source.slice(index, index + 2).join('\n\n'),
    });
  }
  return chapters;
}

function getProcessSteps(post, paragraphs) {
  const chapters = getChapters(post, paragraphs).slice(0, 6);
  const labels = ['Baseline the issue', 'Map the handoff', 'Assign ownership', 'Review the rhythm'];
  return chapters.map((chapter, index) => (
    Array.isArray(post.chapters) && post.chapters.length
      ? chapter
      : { ...chapter, heading: labels[index] || chapter.heading }
  ));
}

function LoadingState({ status, error }) {
  return (
    <div className="min-h-screen bg-canvas pt-32 text-center text-ink">
      {status === 'loading' ? (
        <div className="inline-flex items-center gap-3 text-copy/58">
          <Loader2 className="h-5 w-5 animate-spin text-accent-ink" />
          <span className="font-body text-sm">Loading article...</span>
        </div>
      ) : status === 'error' ? (
        <div className="site-content-frame border border-red-500/20 bg-red-500/5 p-8 text-left">
          <p className="font-body text-sm text-red-200">{error}</p>
        </div>
      ) : (
        <div className="site-content-shell">
          <h1 className="mb-5 font-serif text-5xl leading-[0.92] tracking-tight text-ink md:text-7xl">Article not found.</h1>
          <p className="text-copy/68">This article is not published or does not exist.</p>
        </div>
      )}
    </div>
  );
}

function MetaBar({ items, panelBg }) {
  return (
    <section className="border-y border-ink/10" style={{ backgroundColor: `var(--editorial-panel, ${panelBg})` }}>
      <div className="site-content-shell grid grid-cols-2 gap-y-8 lg:grid-cols-4">
        {items.map(([label, value]) => (
          <div key={label} className="py-8 pr-6 lg:border-r lg:border-ink/10 lg:last:border-0">
            <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.25em] text-tone-500">{label}</div>
            <div className="font-serif text-lg text-ink lg:text-xl">{value}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function EditorialHero({ post, theme, accent, heroImage, heroRef, imageScale, imageY, titleY }) {
  const heroScrollMotion = useHeroScrollMotion();
  return (
    <section data-theme-surface="dark" ref={heroRef} className="site-image-hero relative h-[90svh] overflow-hidden grain" data-testid="blog-theme-editorial-parallax">
      <HeroBackground>
        <motion.div style={heroScrollMotion ? { scale: imageScale, y: imageY } : { scale: 1, y: 0 }} className={`absolute inset-0 ${heroScrollMotion ? 'will-change-transform' : ''}`}>
          <img src={heroImage} alt={post.featuredImageAlt || ''} className="h-full w-full object-cover" />
        </motion.div>
      </HeroBackground>
      <motion.div style={{ y: heroScrollMotion ? titleY : 0 }} className="site-content-shell relative z-10 flex h-full flex-col justify-end pb-16">
        <motion.span initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} className="mb-6 font-mono text-[11px] uppercase tracking-[0.3em]" style={{ color: `color-mix(in srgb, ${accent} var(--accent-strength, 100%), black)` }}>
          {theme.label} / {post.category || 'Blog'}
        </motion.span>
        <motion.h1 initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35, duration: 1, ease: [0.16, 1, 0.3, 1] }} className="max-w-5xl font-serif text-4xl font-light leading-[0.9] tracking-tighter text-ink sm:text-6xl lg:text-8xl">
          {post.title}
        </motion.h1>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.65 }} className="mt-8 max-w-2xl text-lg leading-relaxed text-copy/76">
          {post.excerpt}
        </motion.p>
      </motion.div>
    </section>
  );
}

function SplitHero({ post, theme, accent, heroImage, heroStat }) {
  return (
    <section className="grid min-h-screen grid-cols-1 lg:grid-cols-2" data-testid="blog-theme-wine-split">
      <div className="relative flex flex-col justify-center overflow-hidden px-6 py-32 md:px-12 lg:px-20" style={{ backgroundColor: `var(--editorial-panel, ${theme.panelBg})` }}>
        <Watermark className="bottom-4 right-4 text-[16rem] text-ink/[0.04]">{theme.watermark}</Watermark>
        <motion.span initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} className="relative mb-8 font-mono text-[11px] uppercase tracking-[0.3em]" style={{ color: `color-mix(in srgb, ${accent} var(--accent-strength, 100%), black)` }}>
          {theme.label} / {post.category || 'Blog'}
        </motion.span>
        <motion.h1 initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35, duration: 1, ease: [0.16, 1, 0.3, 1] }} className="relative font-serif text-5xl font-light leading-[0.92] tracking-tight text-ink md:text-7xl">
          {post.title}
        </motion.h1>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.65 }} className="relative mt-8 max-w-md text-lg leading-relaxed text-copy/76">
          {post.excerpt}
        </motion.p>
        <div className="relative mt-10 font-serif text-6xl md:text-8xl" style={{ color: `color-mix(in srgb, ${accent} var(--accent-strength, 100%), black)` }}>{heroStat.value}</div>
        <div className="relative mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-copy/55">{heroStat.label}</div>
      </div>
      <div className="relative min-h-[50svh] overflow-hidden">
        <motion.img initial={{ scale: 1.2 }} animate={{ scale: 1 }} transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }} src={heroImage} alt={post.featuredImageAlt || ''} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 mix-blend-multiply" style={{ backgroundColor: theme.panelBg, opacity: 0.35 }} />
      </div>
    </section>
  );
}

function DataHero({ post, theme, accent, heroStat, metrics }) {
  return (
    <section className="relative overflow-hidden border-b border-ink/10 pt-40 pb-16" data-testid="blog-theme-data-room">
      <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: 'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)', backgroundSize: '48px 48px' }} />
      <div className="site-content-shell relative">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-6 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.3em]" style={{ color: `color-mix(in srgb, ${accent} var(--accent-strength, 100%), black)` }}>
          <span className="h-2 w-2 rounded-full animate-pulse" style={{ backgroundColor: accent }} /> {theme.label} / {post.category || 'Blog'}
        </motion.div>
        <motion.h1 initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }} className="max-w-4xl font-serif text-5xl font-light leading-[0.9] tracking-tighter text-ink md:text-8xl">
          {post.title}
        </motion.h1>
        <p className="mt-8 max-w-2xl font-mono text-lg text-tone-300">// {post.excerpt}</p>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="mt-12 flex flex-wrap gap-3">
          {[[heroStat.value, heroStat.label], ...metrics.slice(1, 3)].map(([value, label]) => (
            <div key={`${value}-${label}`} className="flex items-baseline gap-3 rounded-lg border border-ink/10 bg-surface/40 px-5 py-3">
              <span className="font-mono text-2xl tabular-nums" style={{ color: `color-mix(in srgb, ${accent} var(--accent-strength, 100%), black)` }}>{value}</span>
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-tone-500">{label}</span>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function ProcessHero({ post, theme, accent }) {
  return (
    <section className="relative overflow-hidden border-b border-ink/10 pt-40 pb-16" data-testid="blog-theme-process-index">
      <GridGlow accent={accent} />
      <div className="site-content-shell relative">
        <motion.span initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-6 block font-mono text-[11px] uppercase tracking-[0.3em]" style={{ color: `color-mix(in srgb, ${accent} var(--accent-strength, 100%), black)` }}>
          {theme.label} / {post.category || 'Blog'}
        </motion.span>
        <motion.h1 initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }} className="max-w-5xl font-serif text-5xl font-light leading-[0.9] tracking-tighter text-ink md:text-7xl lg:text-8xl">
          {post.title}
        </motion.h1>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="mt-8 max-w-2xl text-lg leading-relaxed text-tone-300 md:text-xl">
          {post.excerpt}
        </motion.p>
      </div>
    </section>
  );
}

function MetricsSection({ metrics, accent, panelBg, dataRoom = false }) {
  const icons = [Activity, Building2, Users, LineChart];
  return (
    <section className="border-y border-ink/10" style={{ backgroundColor: `var(--editorial-panel, ${panelBg})` }}>
      <motion.div variants={staggerContainer} initial="hidden" whileInView="show" viewport={viewportOnce} className="site-content-shell grid grid-cols-2 lg:grid-cols-4">
        {metrics.map(([value, label], index) => {
          const Icon = icons[index % icons.length];
          return (
            <motion.div key={`${value}-${label}`} variants={fadeUp} className="py-10 pr-8 lg:border-r lg:border-ink/10 lg:last:border-0">
              {dataRoom ? <Icon className="mb-5 h-5 w-5" style={{ color: `color-mix(in srgb, ${accent} var(--accent-strength, 100%), black)` }} /> : null}
              <div className={`${dataRoom ? 'font-mono text-4xl lg:text-5xl' : 'font-serif text-5xl lg:text-7xl'} leading-none text-ink`} style={!dataRoom ? { color: `color-mix(in srgb, ${accent} var(--accent-strength, 100%), black)` } : undefined}>{value}</div>
              <div className="mt-3 font-mono text-[11px] uppercase tracking-[0.2em] text-tone-500">{label}</div>
            </motion.div>
          );
        })}
      </motion.div>
    </section>
  );
}

function PillarsSection({ pillars, accent, heading }) {
  const sectionHeading = String(heading || '').trim() || 'How To Read It';
  return (
    <section className="site-content-shell relative overflow-hidden py-24 md:py-32">
      <GridGlow accent={accent} />
      <motion.h2 variants={fadeUp} initial="hidden" whileInView="show" viewport={viewportOnce} className="relative mb-14 font-serif text-3xl font-light tracking-tight text-ink md:text-5xl">
        {sectionHeading}
      </motion.h2>
      <motion.div variants={staggerContainer} initial="hidden" whileInView="show" viewport={viewportOnce} className="relative grid grid-cols-1 gap-5 md:grid-cols-3">
        {pillars.map((pillar, index) => {
          const Icon = pillar.icon;
          return (
            <motion.div key={pillar.title} variants={fadeUp} className="group rounded-2xl border border-ink/10 bg-ink/[0.02] p-8 transition-colors" whileHover={{ borderColor: `${accent}66` }}>
              <div className="mb-6 flex items-center justify-between">
                <Icon className="h-6 w-6" style={{ color: `color-mix(in srgb, ${accent} var(--accent-strength, 100%), black)` }} />
                <span className="font-mono text-sm text-tone-700">0{index + 1}</span>
              </div>
              <h3 className="font-serif text-2xl text-ink md:text-3xl">{pillar.title}</h3>
              <p className="mt-3 leading-relaxed text-tone-400">{pillar.detail}</p>
            </motion.div>
          );
        })}
      </motion.div>
    </section>
  );
}

function ChapterSections({ chapters, accent }) {
  return chapters.map((chapter) => (
    <section key={chapter.number} className="site-content-shell border-b border-ink/10 py-16 md:py-24">
      <motion.div variants={staggerContainer} initial="hidden" whileInView="show" viewport={viewportOnce} className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-16">
        <motion.div variants={fadeUp} className="lg:col-span-4">
          <span className="font-mono text-sm text-tone-600">{chapter.eyebrow || chapter.number}</span>
          <h2 className="mt-2 font-serif text-3xl text-ink md:text-4xl">{chapter.heading}</h2>
          {chapter.leftText ? (
            <div className="mt-6 space-y-4">
              {renderParagraphs(chapter.leftText).map((paragraph) => (
                <p key={paragraph} className="font-mono text-[11px] leading-6 text-tone-500">{paragraph}</p>
              ))}
            </div>
          ) : null}
        </motion.div>
        <motion.div variants={fadeUp} className="lg:col-span-8 space-y-6">
          {renderParagraphs(chapter.body).map((paragraph) => (
            <p key={paragraph} className="text-lg leading-relaxed text-tone-200 md:text-xl">{paragraph}</p>
          ))}
        </motion.div>
      </motion.div>
    </section>
  ));
}

function DeliverablesSection({ deliverables, accent, panelBg }) {
  if (!deliverables?.length) return null;
  return (
    <section className="border-y border-ink/10 py-20 md:py-28" style={{ backgroundColor: `var(--editorial-panel, ${panelBg})` }}>
      <div className="site-content-shell">
        <motion.div variants={staggerContainer} initial="hidden" whileInView="show" viewport={viewportOnce} className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {deliverables.map((item) => (
            <motion.div key={item} variants={fadeUp} className="flex items-start gap-4 border border-ink/10 bg-surface/25 p-5">
              <Check className="mt-1 h-4 w-4 shrink-0" style={{ color: `color-mix(in srgb, ${accent} var(--accent-strength, 100%), black)` }} />
              <p className="text-sm leading-6 text-tone-300 md:text-base">{item}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function ProcessBody({ steps, metrics, deliverables, accent }) {
  return (
    <div className="site-content-shell grid grid-cols-1 gap-12 py-20 md:py-28 lg:grid-cols-12 lg:gap-16">
      <aside className="lg:col-span-3">
        <nav className="space-y-3 lg:sticky lg:top-28" data-testid="blog-process-index">
          {steps.map((step, index) => (
            <a key={step.number} href={`#blog-step-${step.number}`} className="group flex items-center gap-3 text-left">
              <span className="font-mono text-[10px] text-tone-600">{String(index + 1).padStart(2, '0')}</span>
              <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-tone-400 transition-colors group-hover:text-ink">{step.heading}</span>
            </a>
          ))}
        </nav>
      </aside>
      <div className="space-y-24 lg:col-span-9">
        <section>
          <h2 className="mb-6 font-serif text-3xl text-ink md:text-4xl">Context</h2>
          <p className="max-w-3xl text-lg leading-relaxed text-tone-300 md:text-xl">{renderParagraphs(steps[0]?.body || '')[0] || 'This note is being prepared.'}</p>
        </section>
        <section>
          <h2 className="mb-12 font-serif text-3xl text-ink md:text-4xl">The Process</h2>
          <motion.ol variants={staggerContainer} initial="hidden" whileInView="show" viewport={viewportOnce} className="relative space-y-12 border-l border-ink/15 pl-8">
            {steps.map((step, index) => (
              <motion.li key={step.number} id={`blog-step-${step.number}`} variants={fadeUp} className="relative scroll-mt-28">
                <span className="absolute -left-[2.65rem] grid h-8 w-8 place-items-center rounded-full border bg-canvas font-mono text-xs" style={{ borderColor: accent, color: `color-mix(in srgb, ${accent} var(--accent-strength, 100%), black)` }}>{index + 1}</span>
                <h3 className="mb-2 font-serif text-2xl text-ink">{step.heading}</h3>
                <p className="max-w-2xl leading-relaxed text-tone-400">{renderParagraphs(step.body).join(' ')}</p>
              </motion.li>
            ))}
          </motion.ol>
        </section>
        <section>
          <h2 className="mb-12 font-serif text-3xl text-ink md:text-4xl">Signals</h2>
          <div className="grid grid-cols-2 gap-y-10 gap-x-6 lg:grid-cols-4">
            {metrics.map(([value, label]) => (
              <div key={`${value}-${label}`}>
                <div className="font-serif text-5xl leading-none lg:text-6xl" style={{ color: `color-mix(in srgb, ${accent} var(--accent-strength, 100%), black)` }}>{value}</div>
                <div className="mt-3 font-mono text-[11px] uppercase tracking-[0.2em] text-tone-500">{label}</div>
              </div>
            ))}
          </div>
        </section>
        {deliverables?.length ? (
          <section>
            <h2 className="mb-8 font-serif text-3xl text-ink md:text-4xl">What this gives you</h2>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {deliverables.map((item) => (
                <div key={item} className="flex items-start gap-4 border border-ink/10 bg-ink/[0.02] p-5">
                  <Check className="mt-1 h-4 w-4 shrink-0" style={{ color: `color-mix(in srgb, ${accent} var(--accent-strength, 100%), black)` }} />
                  <p className="text-sm leading-6 text-tone-300">{item}</p>
                </div>
              ))}
            </div>
          </section>
        ) : null}
      </div>
    </div>
  );
}

function PullQuote({ post, accent, paragraphs }) {
  const quote = post.pullQuote || post.heroStatLabel || getOneSentence(post.excerpt, paragraphs[0]);
  const source = post.pullQuoteSource || post.authorName || 'Magellan';
  return (
    <section className="bg-canvas py-24 md:py-32">
      <div className="site-content-shell">
        <motion.blockquote variants={fadeUp} initial="hidden" whileInView="show" viewport={viewportOnce} className="font-serif text-3xl font-light italic leading-[1.2] text-ink md:text-5xl">
          "{quote}"
        </motion.blockquote>
        <div className="mt-8 font-mono text-[11px] uppercase tracking-[0.25em] text-tone-500" style={{ color: `color-mix(in srgb, ${accent} var(--accent-strength, 100%), black)` }}>
          {source}
        </div>
      </div>
    </section>
  );
}

function TagsSection({ tags, accent }) {
  if (!tags?.length) return null;
  return (
    <section className="border-y border-ink/10 bg-canvas py-16">
      <div className="site-content-shell flex flex-wrap gap-3">
        {tags.map((tag) => (
          <span key={tag} className="rounded-full border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em]" style={{ borderColor: `${accent}55`, color: `color-mix(in srgb, ${accent} var(--accent-strength, 100%), black)` }}>
            {tag}
          </span>
        ))}
      </div>
    </section>
  );
}

export default function BlogPostPage() {
  const { slug } = useParams();
  return <BlogPost key={slug} />;
}
function BlogPost() {
  const { slug } = useParams();
  const heroRef = useRef(null);
  const [post, setPost] = useState(null);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const imageScale = useTransform(scrollYProgress, [0, 1], [1, 1.2]);
  const imageY = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const titleY = useTransform(scrollYProgress, [0, 1], ['0%', '40%']);

  useEffect(() => {
    let mounted = true;
    setPost(null);
    setError('');
    setStatus('loading');

    getPublishedBlogPostBySlug(slug)
      .then((loadedPost) => {
        if (!mounted) return;
        setPost(loadedPost);
        setStatus(loadedPost ? 'ready' : 'missing');
      })
      .catch((loadError) => {
        if (!mounted) return;
        setError(loadError?.message || 'Unable to load this article.');
        setStatus('error');
      });

    return () => {
      mounted = false;
    };
  }, [slug]);

  const viewModel = useMemo(() => {
    if (!post) return null;
    const themeKey = sanitizeTheme(post.visualTheme);
    const theme = { ...THEME_DETAILS[themeKey], key: themeKey };
    const accent = sanitizeAccent(post.accentColor, themeKey);
    const paragraphs = renderParagraphs(post.body);
    const heroImage = post.featuredImageUrl || DEFAULT_HERO_IMAGE;
    const metaItems = getMetaItems(post, paragraphs);
    const heroStat = getHeroStat(post, paragraphs);
    const metrics = getMetrics(post, paragraphs);
    const pillars = getPillars(post, paragraphs);
    const chapters = getChapters(post, paragraphs);
    const processSteps = getProcessSteps(post, paragraphs);
    const deliverables = Array.isArray(post.deliverables) ? post.deliverables.filter(Boolean) : [];
    return { theme, accent, paragraphs, heroImage, metaItems, heroStat, metrics, pillars, chapters, processSteps, deliverables };
  }, [post]);

  if (status !== 'ready' || !post || !viewModel) {
    return (
      <div className="min-h-screen bg-canvas text-ink">
        <CaseNav backLabel="Selected Work" backTo="/blog" actionLabel="Reserve" actionTo="/#reserve" />
        <LoadingState status={status} error={error} />
      </div>
    );
  }

  const { theme, accent, paragraphs, heroImage, metaItems, heroStat, metrics, pillars, chapters, processSteps, deliverables } = viewModel;
  const isDataRoom = theme.key === 'data-room';

  return (
    <div className="min-h-screen text-ink" style={{ backgroundColor: `var(--editorial-page, ${theme.pageBg})` }} data-testid={`blog-post-page-${theme.key}`}>
      <CaseNav backLabel="Selected Work" backTo="/blog" actionLabel="Reserve" actionTo="/#reserve" />
      <Link to="/blog" className="fixed left-6 top-24 z-40 hidden items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-copy/58 transition-colors hover:text-ink lg:inline-flex">
        <ArrowLeft className="h-4 w-4" />
        Blog
      </Link>

      {theme.heroMode === 'parallax' ? (
        <EditorialHero post={post} theme={theme} accent={accent} heroImage={heroImage} heroRef={heroRef} imageScale={imageScale} imageY={imageY} titleY={titleY} />
      ) : theme.heroMode === 'split' ? (
        <SplitHero post={post} theme={theme} accent={accent} heroImage={heroImage} heroStat={heroStat} />
      ) : theme.heroMode === 'terminal' ? (
        <DataHero post={post} theme={theme} accent={accent} heroStat={heroStat} metrics={metrics} />
      ) : (
        <ProcessHero post={post} theme={theme} accent={accent} />
      )}

      <MetaBar items={metaItems} panelBg={theme.panelBg} />

      {theme.key === 'process-index' ? (
        <ProcessBody steps={processSteps} metrics={metrics} deliverables={deliverables} accent={accent} />
      ) : (
        <>
          <section className="site-content-shell relative overflow-hidden py-24 md:py-32">
            <Watermark className="top-1/2 right-[-2%] hidden -translate-y-1/2 text-[26vw] lg:block">{theme.watermark}</Watermark>
            <motion.p variants={fadeUp} initial="hidden" whileInView="show" viewport={viewportOnce} className="relative max-w-4xl font-serif text-3xl font-light leading-[1.15] tracking-tight text-ink md:text-5xl">
              {getOneSentence(post.excerpt, paragraphs[0]) || post.title}
            </motion.p>
          </section>
          <MetricsSection metrics={metrics} accent={accent} panelBg={theme.panelBg} dataRoom={isDataRoom} />
          <PillarsSection pillars={pillars} accent={accent} heading={post.readingSectionHeading} />
          <ChapterSections chapters={chapters} accent={accent} />
          <DeliverablesSection deliverables={deliverables} accent={accent} panelBg={theme.panelBg} />
          <PullQuote post={post} accent={accent} paragraphs={paragraphs} />
        </>
      )}

      <TagsSection tags={post.tags} accent={accent} />

      <section className="bg-canvas py-20 md:py-28">
        <div className="site-content-shell text-center">
          <Link to="/blog" className="group inline-flex flex-col items-center">
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-tone-500">Back to selected work</span>
            <span className="mt-3 inline-flex items-center gap-3 font-serif text-3xl text-ink transition-colors group-hover:text-accent-ink md:text-5xl">
              Read more notes
              <ArrowLeft className="h-7 w-7 transition-transform group-hover:-translate-x-2" />
            </span>
          </Link>
        </div>
      </section>
    </div>
  );
}
